import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2, Filter, Users } from "lucide-react";

const FIELD_LABELS = { date: "Date", product_name: "Product", city: "City", pin_code: "Pin code", name: "Name", email: "Email", phone: "Phone", order_status: "Status" };
const OP_LABELS = { between: "between", before: "before", after: "after", contains: "contains", equals: "is", is: "is", starts_with: "starts with", is_one_of: "is one of", is_present: "is present" };

function condLabel(c) {
  const v = c.operator === "between" ? `${c.value?.from || "…"} → ${c.value?.to || "…"}` : c.operator === "is_present" ? "" : c.value;
  return `${FIELD_LABELS[c.field] || c.field} ${OP_LABELS[c.operator] || c.operator} ${v || ""}`.trim();
}

export default function AudiencesPage() {
  const [items, setItems] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get("/audiences").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    await api.delete(`/audiences/${id}`);
    toast.success("Audience deleted");
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-indigo-400 mb-1">Saved segments — re-evaluated at send time</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-50">Audiences</h1>
        </div>
        <Button data-testid="new-audience-button" onClick={() => navigate("/audiences/new")} className="bg-indigo-500 hover:bg-indigo-600 text-white">
          <Plus size={15} className="mr-1.5" /> New audience
        </Button>
      </div>

      {items === null ? null : items.length === 0 ? (
        <div className="bg-[#111827] border border-slate-800 rounded-xl p-12 text-center" data-testid="audiences-empty-state">
          <Filter size={32} className="mx-auto text-slate-600 mb-3" />
          <p className="text-slate-400 mb-4">No audiences yet. Build your first targeted segment.</p>
          <Link to="/audiences/new" className="text-indigo-400 hover:text-indigo-300 text-sm">Create one →</Link>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((a) => (
            <div key={a.id} data-testid="audience-card" className="stat-card p-5 flex flex-col fade-up">
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-heading font-semibold text-slate-100">{a.name}</h3>
                <div className="flex gap-1">
                  <button data-testid="audience-edit-button" onClick={() => navigate(`/audiences/${a.id}/edit`)} className="text-slate-500 hover:text-indigo-400 p-1"><Pencil size={14} /></button>
                  <button data-testid="audience-delete-button" onClick={() => remove(a.id)} className="text-slate-500 hover:text-rose-400 p-1"><Trash2 size={14} /></button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 mb-4 flex-1">
                {(a.conditions || []).length === 0 ? (
                  <span className="text-xs text-slate-500">All customers</span>
                ) : a.conditions.map((c, i) => (
                  <span key={i} className="text-[11px] font-mono px-2 py-0.5 rounded bg-slate-800 text-slate-300 border border-slate-700">{condLabel(c)}</span>
                ))}
              </div>
              <div className="flex items-center gap-2 text-emerald-400">
                <Users size={14} />
                <span data-testid="audience-count" className="font-mono text-sm font-semibold">{a.count} matching</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
